import { z } from "zod";
import { expectedVersion, id, pageInput } from "./common";
import { isAgencyRole, type WorkspaceRole } from "./roles";
import { longText } from "./text";

/**
 * A campaign brief is a series of revisions (ADR 0015). Either side drafts one, then sends it
 * (immutable); the other side approves it or asks for changes with clarification questions.
 */
export const BRIEF_STATUSES = [
  "draft",
  "submitted",
  "accepted",
  "clarification_requested",
] as const;
export type BriefStatus = (typeof BRIEF_STATUSES)[number];

export const BRIEF_STATUS_LABELS: Record<BriefStatus, string> = {
  draft: "Draft",
  submitted: "Waiting for approval",
  accepted: "Approved",
  clarification_requested: "Clarification requested",
};

/** Which side of the workspace wrote a revision. */
export const BRIEF_SIDES = ["agency", "client"] as const;
export type BriefSide = (typeof BRIEF_SIDES)[number];

export const sideOf = (role: WorkspaceRole): BriefSide =>
  isAgencyRole(role) ? "agency" : "client";
export const otherSide = (side: BriefSide): BriefSide => (side === "agency" ? "client" : "agency");

/** Reads after a verb: "Send to the agency". */
export const BRIEF_SIDE_LABELS: Record<BriefSide, string> = {
  agency: "the agency",
  client: "the client",
};

/** Who writes, sends and answers questions for each side (the `brief.edit` holders). */
export const BRIEF_WRITERS: Record<BriefSide, readonly WorkspaceRole[]> = {
  agency: ["agency_owner", "agency_admin", "strategist"],
  client: ["client_owner"],
};

/** Who approves or asks for changes on the other side's revisions (the `brief.decide` holders). */
export const BRIEF_DECIDERS: Record<BriefSide, readonly WorkspaceRole[]> = {
  agency: ["agency_owner", "agency_admin"],
  client: ["client_owner"],
};

/** The guided brief: what each section asks for, in form order. */
export const BRIEF_FIELDS = [
  "summary",
  "goals",
  "keyMessages",
  "deliverables",
  "mandatories",
  "constraints",
  "successMetrics",
  "references",
] as const;
export type BriefField = (typeof BRIEF_FIELDS)[number];

export const BRIEF_FIELD_GUIDE: Record<BriefField, { label: string; hint: string }> = {
  summary: { label: "Summary", hint: "The campaign in two or three sentences." },
  goals: { label: "Goals", hint: "What success looks like, with numbers where possible." },
  keyMessages: { label: "Key messages", hint: "What the audience should take away." },
  deliverables: {
    label: "Deliverables",
    hint: "What will be produced, how many, for which channel.",
  },
  mandatories: { label: "Mandatories", hint: "Brand must-haves: logo use, tone, legal lines." },
  constraints: { label: "Constraints", hint: "What to avoid: claims, topics, competitors." },
  successMetrics: { label: "Success metrics", hint: "How results will be measured and reported." },
  references: { label: "References", hint: "Examples, links, previous campaigns." },
};

/** Sections a brief can't be sent without. */
export const BRIEF_REQUIRED_FOR_SUBMIT = ["summary", "goals", "deliverables"] as const;

export const briefSubmitIssues = (brief: Partial<Record<BriefField, string>>) =>
  BRIEF_REQUIRED_FOR_SUBMIT.filter((field) => !(brief[field] ?? "").trim()).map((field) => ({
    field,
    message: `${BRIEF_FIELD_GUIDE[field].label} is needed before sending`,
  }));

const section = longText(4000).optional();

/** First save creates the next revision; later saves update the open draft. */
export const saveBriefDraftInput = z.object({
  campaignId: id,
  /** Required when a draft already exists: the version you edited. */
  expectedVersion: expectedVersion.optional(),
  summary: section,
  goals: section,
  keyMessages: section,
  deliverables: section,
  mandatories: section,
  constraints: section,
  successMetrics: section,
  references: section,
});
export type SaveBriefDraftInput = z.infer<typeof saveBriefDraftInput>;

export const briefRevisionActionInput = z.object({ revisionId: id, expectedVersion });

export const acceptBriefInput = briefRevisionActionInput.extend({
  note: longText(2000).optional(),
});

export const requestClarificationInput = briefRevisionActionInput.extend({
  note: longText(2000).optional(),
  questions: z.array(longText(1000).min(1, "Write the question")).min(1).max(10),
});

export const answerBriefQuestionInput = z.object({
  questionId: id,
  answer: longText(4000).min(1, "Write the answer"),
  expectedVersion,
});

export const listBriefRevisionsInput = pageInput.extend({ campaignId: id });
export type ListBriefRevisionsInput = z.infer<typeof listBriefRevisionsInput>;
